import type * as React from "react";
import { useState, useRef, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { SearchInput } from "src/common/components/SearchInput";

interface IItems {
  name: string;
}

interface ISearchAutocompleteProps {
  items: IItems[];
  handleOnSearch: (value: string) => void;
  handleOnSelect: (item: IItems) => void;
  placeholder?: string;
}

export const SearchAutocomplete: React.FC<ISearchAutocompleteProps> = ({
  handleOnSearch,
  handleOnSelect,
  items,
  placeholder,
}: ISearchAutocompleteProps) => {
  const { t } = useTranslation();
  const [input, setInput] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);

  const wrapperRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const filteredItems = useMemo(() => {
    const q = input.trim().toLowerCase();
    if (!q) return [];
    return items.filter((item) => item.name.toLowerCase().includes(q)).slice(0, 10);
  }, [input, items]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setShowSuggestions(false);
        setHighlightedIndex(-1);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (highlightedIndex >= 0 && listRef.current) {
      const el = listRef.current.children[highlightedIndex] as HTMLElement;
      el?.scrollIntoView({ block: "nearest" });
    }
  }, [highlightedIndex]);

  const handleInputChange = (value: string) => {
    setInput(value);
    handleOnSearch(value);
    if (value.trim()) {
      setShowSuggestions(true);
      setHighlightedIndex(-1);
    } else {
      setShowSuggestions(false);
    }
  };

  const handleSelect = (item: IItems) => {
    setInput(item.name);
    handleOnSelect(item);
    setShowSuggestions(false);
    setHighlightedIndex(-1);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showSuggestions || filteredItems.length === 0) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex((prev) => Math.min(prev + 1, filteredItems.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === "Enter" && highlightedIndex >= 0) {
      e.preventDefault();
      handleSelect(filteredItems[highlightedIndex]);
    } else if (e.key === "Escape") {
      setShowSuggestions(false);
      setHighlightedIndex(-1);
    }
  };

  return (
    <header className="relative z-50">
      <div ref={wrapperRef} style={{ width: 250 }} className="relative">
        <SearchInput
          value={input}
          onChange={handleInputChange}
          onFocus={() => {
            if (filteredItems.length > 0) setShowSuggestions(true);
          }}
          onKeyDown={handleKeyDown}
          placeholder={placeholder || t("general.search")}
        />

        {showSuggestions && (
          <div
            ref={listRef}
            className="absolute top-full left-0 w-full bg-white border border-gray-300 rounded-lg mt-[1px] z-50 shadow-md max-h-64 overflow-y-auto"
          >
            {filteredItems.length > 0 ? (
              filteredItems.map((item, idx) => {
                const isActive = idx === highlightedIndex;
                return (
                  <button
                    key={`${item.name}-${idx}`}
                    onClick={() => handleSelect(item)}
                    className={`w-full text-left px-3 py-2 text-sm ${
                      isActive ? "bg-gray-100" : "hover:bg-gray-50"
                    }`}
                    type="button"
                  >
                    {item.name}
                  </button>
                );
              })
            ) : (
              <div className="text-sm text-gray-500 px-4 py-3 italic">{t("general.noResults")}</div>
            )}
          </div>
        )}
      </div>
    </header>
  );
};
